/**
 * Turns a flat page of notifications into the grouped centre view model:
 * module (domain) -> subdomain block -> row, with repeated updates about one
 * object coalesced into a single expandable thread row.
 *
 * Grouping is derived, not guessed:
 *  1. `domain` / `subdomain` from the row when the template pipeline wrote
 *     them; otherwise the catalogue-derived legacy map.
 *  2. Coalescing prefers the server's `thread_id`, then its `coalesce_key`,
 *     and only falls back to an object reference parsed out of the copy
 *     (`NCR-0042`, `PO 45231`) for rows that predate both.
 *
 * When `GET /notifications/grouped/` lands server-side the same view model can
 * be produced by adapting `NotificationDomainGroup[]` — every consumer below
 * reads these interfaces, not the raw API shape.
 */
import type { LucideIcon } from 'lucide-react';

import type { Notification, NotificationDomain, NotificationSeverity } from '../../lib/api';
import { domainOrder, getDomainMeta, isNotificationDomain } from './domainMeta';
import { getLegacyTypeMeta, getSubdomainLabel } from './legacyTypeMeta';
import { highestSeverity, resolveSeverity } from './severity';

/** One row in the centre: a single notification, or a coalesced thread. */
export interface NotificationCentreEntry {
  kind: 'notification' | 'thread';
  /** Stable React key. */
  key: string;
  /** Row headline — the latest title, or "NCR-0042 — 6 updates" for a thread. */
  headline: string;
  message: string;
  severity: NotificationSeverity;
  actionUrl: string;
  timeAgo: string;
  lastActivityAt: string;
  isRead: boolean;
  unreadCount: number;
  itemCount: number;
  /** Newest first. Length 1 for a single row. */
  notifications: Notification[];
  /** Parsed object reference when one was found, e.g. `NCR-0042`. */
  reference: string;
  /** `notification_type` of the newest row — drives the row icon. */
  iconType: string;
}

/** One subdomain block (Purchase Orders, NCRs, ...) inside a module section. */
export interface NotificationSubGroup {
  key: string;
  subdomain: string;
  label: string;
  entries: NotificationCentreEntry[];
  totalCount: number;
  unreadCount: number;
  highestSeverity: NotificationSeverity;
}

/** One collapsible module section (Purchasing, Quality, ...). */
export interface NotificationModuleGroup {
  domain: NotificationDomain;
  label: string;
  icon: LucideIcon;
  emptyHint: string;
  subgroups: NotificationSubGroup[];
  totalCount: number;
  unreadCount: number;
  actionRequiredCount: number;
  highestSeverity: NotificationSeverity;
  /** Ids of every unread notification in the section (mark-group-read). */
  unreadIds: number[];
  lastActivityAt: string;
}

/**
 * Object references the copy uses today, e.g. "NCR-0042", "PO 45231",
 * "GRN-1446248", "NCR00001". The suffix needs three or more characters so a
 * bare word ("PO for") can never masquerade as a document number, and it
 * must contain a digit — without that, "NCR status changed" and "RFQ
 * cancelled" parsed as documents and the centre showed thread rows headed
 * "NCR-STATUS — 3 updates" / "RFQ-CANCELLED — 2 updates".
 */
const REFERENCE_PREFIXES = 'NCR|CAR|SCAR|PO|GRN|ASN|RFQ|CONC|DN|INV';
const REFERENCE_PATTERN = new RegExp(
  `\\b(${REFERENCE_PREFIXES})[\\s#-]?((?=[A-Z0-9/-]*\\d)[A-Z0-9][A-Z0-9/-]{2,})\\b`,
  'i',
);
/** "NCR NCR00001": the suffix is already a full number — use it alone. */
const SELF_PREFIXED_SUFFIX = new RegExp(`^(${REFERENCE_PREFIXES})[#-]?\\d`, 'i');

/**
 * Extracts the object reference from a row's copy, or `''`. Returned
 * verbatim — "NCR00001" stays "NCR00001", the number users see everywhere
 * else — so the headline never invents a separator the document lacks.
 * Use {@link referenceKey} when comparing references.
 */
export function extractReference(notification: Notification): string {
  const haystack = `${notification.title ?? ''} ${notification.message ?? ''}`;
  const match = REFERENCE_PATTERN.exec(haystack);
  if (!match) return '';
  const suffix = match[2] ?? '';
  const reference = SELF_PREFIXED_SUFFIX.test(suffix) ? suffix : (match[0] ?? '');
  return reference.trim().replace(/[-\s]+$/, '');
}

/** Normalised form of a reference for bucketing: "PO 45231" == "PO-45231". */
export function referenceKey(reference: string): string {
  return reference.toUpperCase().replace(/[\s#-]+/g, '');
}

/** Domain a row belongs to — its own when set, else the legacy map. */
export function resolveDomain(notification: Notification): NotificationDomain {
  const own = notification.domain;
  if (own && isNotificationDomain(own)) return own;
  return getLegacyTypeMeta(notification.notification_type).domain;
}

/** Subdomain slug for a row — its own when set, else the legacy map. */
export function resolveSubdomain(notification: Notification): string {
  const own = notification.grouping_key || notification.subdomain;
  if (own) return own;
  return getLegacyTypeMeta(notification.notification_type).subdomain;
}

/** Label for a subdomain block — the server's display name wins. */
function resolveSubdomainLabel(notification: Notification, subdomain: string): string {
  return notification.subdomain_display || getSubdomainLabel(subdomain);
}

/** Coalescing bucket for a row, or `''` when the row stands alone. */
function coalesceBucket(
  notification: Notification,
  domain: NotificationDomain,
  subdomain: string,
  reference: string
): string {
  if (notification.thread_id) return `t:${notification.thread_id}`;
  if (notification.coalesce_key) return `c:${notification.coalesce_key}`;
  if (reference) return `r:${domain}:${subdomain}:${referenceKey(reference)}`;
  return '';
}

function newestFirst(a: Notification, b: Notification): number {
  const left = a.created_at ?? '';
  const right = b.created_at ?? '';
  if (left === right) return b.id - a.id;
  return left < right ? 1 : -1;
}

/** Builds one entry from the rows that share a coalescing bucket. */
function buildEntry(rows: Notification[], bucketKey: string): NotificationCentreEntry {
  const ordered = rows.slice().sort(newestFirst);
  const latest = ordered[0];
  const severities = ordered.map((row) => resolveSeverity(row));
  const unread = ordered.filter((row) => !row.is_read);
  const reference = extractReference(latest);
  const isThread = ordered.length > 1;
  const headline = isThread
    ? `${reference || latest.title} — ${ordered.length} updates`
    : latest.title;

  return {
    kind: isThread ? 'thread' : 'notification',
    key: isThread ? `thread:${bucketKey}` : `notification:${latest.id}`,
    headline,
    message: latest.message,
    severity: highestSeverity(severities),
    actionUrl: latest.action_url ?? '',
    timeAgo: latest.time_ago,
    lastActivityAt: latest.created_at,
    isRead: unread.length === 0,
    unreadCount: unread.length,
    itemCount: ordered.length,
    notifications: ordered,
    reference,
    iconType: latest.notification_type,
  };
}

function entryOrder(a: NotificationCentreEntry, b: NotificationCentreEntry): number {
  if (a.lastActivityAt === b.lastActivityAt) return b.itemCount - a.itemCount;
  return a.lastActivityAt < b.lastActivityAt ? 1 : -1;
}

/**
 * Groups a page of notifications into module sections.
 *
 * @param notifications - the rows to render, in any order
 * @returns sections in fixed domain order; empty when there is nothing to show
 */
export function groupNotifications(
  notifications: Notification[]
): NotificationModuleGroup[] {
  // domain -> subdomain -> bucket -> rows
  const byDomain = new Map<NotificationDomain, Map<string, Map<string, Notification[]>>>();
  const labels = new Map<string, string>();

  notifications.forEach((notification) => {
    const domain = resolveDomain(notification);
    const subdomain = resolveSubdomain(notification);
    const reference = extractReference(notification);
    const bucket = coalesceBucket(notification, domain, subdomain, reference);
    // Standalone rows get a bucket of their own so they never merge.
    const bucketKey = bucket || `single:${notification.id}`;

    let subdomains = byDomain.get(domain);
    if (!subdomains) {
      subdomains = new Map<string, Map<string, Notification[]>>();
      byDomain.set(domain, subdomains);
    }
    let buckets = subdomains.get(subdomain);
    if (!buckets) {
      buckets = new Map<string, Notification[]>();
      subdomains.set(subdomain, buckets);
      labels.set(`${domain}:${subdomain}`, resolveSubdomainLabel(notification, subdomain));
    }
    const rows = buckets.get(bucketKey);
    if (rows) {
      rows.push(notification);
    } else {
      buckets.set(bucketKey, [notification]);
    }
  });

  const groups: NotificationModuleGroup[] = [];

  byDomain.forEach((subdomains, domain) => {
    const meta = getDomainMeta(domain);
    const subgroups: NotificationSubGroup[] = [];
    const unreadIds: number[] = [];
    let totalCount = 0;
    let unreadCount = 0;
    let actionRequiredCount = 0;
    let lastActivityAt = '';
    const groupSeverities: NotificationSeverity[] = [];

    subdomains.forEach((buckets, subdomain) => {
      const entries: NotificationCentreEntry[] = [];
      buckets.forEach((rows, bucketKey) => {
        entries.push(buildEntry(rows, bucketKey));
      });
      entries.sort(entryOrder);

      const subSeverities = entries.map((entry) => entry.severity);
      const subTotal = entries.reduce((sum, entry) => sum + entry.itemCount, 0);
      const subUnread = entries.reduce((sum, entry) => sum + entry.unreadCount, 0);

      entries.forEach((entry) => {
        entry.notifications.forEach((row) => {
          if (!row.is_read) unreadIds.push(row.id);
          const severity = resolveSeverity(row);
          groupSeverities.push(severity);
          if (severity === 'critical' || severity === 'action_required') {
            actionRequiredCount += 1;
          }
          if (row.created_at > lastActivityAt) lastActivityAt = row.created_at;
        });
      });

      totalCount += subTotal;
      unreadCount += subUnread;
      subgroups.push({
        key: `${domain}:${subdomain}`,
        subdomain,
        label: labels.get(`${domain}:${subdomain}`) ?? getSubdomainLabel(subdomain),
        entries,
        totalCount: subTotal,
        unreadCount: subUnread,
        highestSeverity: highestSeverity(subSeverities),
      });
    });

    subgroups.sort((a, b) => {
      const first = a.entries[0];
      const second = b.entries[0];
      const left = first ? first.lastActivityAt : '';
      const right = second ? second.lastActivityAt : '';
      if (left === right) return a.label.localeCompare(b.label);
      return left < right ? 1 : -1;
    });

    groups.push({
      domain,
      label: meta.label,
      icon: meta.icon,
      emptyHint: meta.emptyHint,
      subgroups,
      totalCount,
      unreadCount,
      actionRequiredCount,
      highestSeverity: highestSeverity(groupSeverities),
      unreadIds,
      lastActivityAt,
    });
  });

  groups.sort((a, b) => domainOrder(a.domain) - domainOrder(b.domain));
  return groups;
}
